import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import {
  INumberEntryProps,
  NumberCard,
} from "@/components/pageCards/numberCard";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import { TerminalAggregate } from "server/dwhApi/terminals/types.js";

type Heading = {
  id: string;
  heading: string;
};

const getMWMData = (
  headings: Heading[],
  data?: TerminalAggregate,
): INumberEntryProps[] => {
  if (!data) {
    return headings.map((h) => ({ heading: h.heading, data: "-", suffix: "" }));
  }
  const {
    totalResidualWasteOccurrences,
    totalOccurrences,
    occurrenceBasedSortingDegree,
  } = data;

  const recycled = totalOccurrences - totalResidualWasteOccurrences;
  const values: { [k: string]: string | number } = {
    residual: totalResidualWasteOccurrences,
    recycled,
    sortingRate: occurrenceBasedSortingDegree * 100,
  };

  const numbers: INumberEntryProps[] = headings.map((h) => ({
    ...h,
    data: values[h.id],
    suffix: h.id === "sortingRate" ? " %" : "",
  }));

  return numbers;
};

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const OverviewKeyNumbersMWM: React.FC<Props> = ({
  timeFilter: persistedTimeFilter,
  setTimeFilters,
}) => {
  const { t } = useTrans();
  const { setKeyNumbersLoading } = useHomeLoadingState();
  const { terminalConfig } = useConfigService();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as any);

  const defaultPeriod = useMemo(
    () =>
      persistedTimeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last30Days,
    [terminalConfig, persistedTimeFilter],
  );

  const filters: FilterSort[] = useMemo(
    () => [createTimeFilters({ defaultValue: defaultPeriod })],
    [defaultPeriod],
  );

  const { currentTerminal } = useTerminalsState();

  const timeFilter = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const { data, isLoading } = trpc.dwh.terminals.getAggregates.useQuery(
    timeFilter,
    {
      queryKeyHashFn: () =>
        [
          "terminalAggregates",
          currentTerminal,
          timeFilter.startTimeISO,
          timeFilter.endTimeISO,
        ].toString(),
    },
  );

  useEffect(() => {
    setKeyNumbersLoading(isLoading);
  }, [setKeyNumbersLoading, isLoading]);

  const headings: Heading[] = useMemo(
    () => [
      {
        id: "residual",
        heading: `${t("circularityLabels:residualWaste")} (${t("statisticsLabels:nrThrows").toLowerCase()})`,
      },
      {
        id: "recycled",
        heading: `${t("circularityLabels:recycled")} (${t("statisticsLabels:nrThrows").toLowerCase()})`,
      },
      { id: "sortingRate", heading: t("circularityLabels:recyclingRate") },
    ],
    [t],
  );

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.KeyNumbers,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  const view = useMemo(() => {
    const numbers = getMWMData(headings, data?.terminalAggregates);

    return (
      <NumberCard
        title="circularityLabels:keyNumbers"
        numbers={numbers}
        filters={filters}
        isLoading={isLoading}
        onFilterOptionChange={onFilterOptionChange}
        filterLocation={CardComponentFilterLocation.RIGHT}
      />
    );
  }, [isLoading, data, filters, headings, onFilterOptionChange]);

  return view;
};
